import React, { Activity, useEffect, useMemo, useState } from 'react';

import {
  getDailyReports,
  getMonthlyReports,
  SetClassesInfo,
  UpdateMonth,
} from '../../features/attendance/AttendanceSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
  TableBody,
  TableCell,
  TableRow,
  Typography,
  TableHead as MuiTableHead,
  Table,
  CircularProgress,
  TableContainer,
  InputAdornment,
  TextField,
  Select,
  MenuItem,
  CardContent,
  Card,
  Box,
  Backdrop,
  Alert,
  Snackbar,
  InputLabel,
  FormControl,
  Button,
} from '@mui/material';
import { SearchIcon } from '@heroicons/react/outline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { getClasses, getMonths } from '../../features/classes/ClassSlice';
import { getLabelsByInstituteType } from '../../utils/constants';
import api from '../../services/api';
import { debounce } from 'lodash-es';
import { RiFileExcel2Line } from 'react-icons/ri';
import { MenuProps } from '../../utils/constants';
import { showAlert } from '../UIElements/AlertService';

export const ReportsPage = React.memo(() => {
  const fullData = useSelector((state) => state.attendance);
  const [AcademicYearMonths, setAcademicYearMonths] = useState('');
  const [buttonType, setButtonType] = useState('daily');
  let specificData = null;
  if (buttonType === 'daily') {
    specificData = fullData?.reportsDaily;
  } else if (buttonType === 'monthly') {
    specificData = fullData?.reportsMonthly;
  }

  const dispatch = useDispatch();
  const [classes, setClasses] = useState(specificData ? 1 : 0);
  const [classDetails, setClassDetails] = useState(
    specificData ? fullData?.classesInformation : [],
  );
  let tempDepartment = [];
  let tempClasses = [];
  let tempSections = [];
  if (specificData) {
    tempDepartment = fullData?.classesInformation?.find(
      (obj) => obj.department === specificData.departmentName,
    );
    tempClasses = tempDepartment?.classes;
    const selectedClass = tempClasses?.find(
      (obj) => obj.className === specificData.className,
    );
    tempSections = selectedClass?.sections;
  }

  const [currentDepartment, setCurrentDepartment] = useState(tempDepartment);
  const [currentClasses, setCurrentClasses] = useState(tempClasses);
  const [currentSections, setCurrentSections] = useState(tempSections);

  const [filteredData, setFilteredData] = useState([]);
  const [searchStudnet, setSearchStudent] = useState('');
  const [students, setStudents] = useState([]);

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const [totalWorkingDays, setTotalWorkingDays] = useState('');

  let { instituteType } = useSelector((state) => {
    return state.auth?.successData?.userDetails ?? {};
  });
  let { Department, Class, Section } = getLabelsByInstituteType(instituteType);
  const [isSearching, setIsSearching] = useState(false); //to show the status of the getting the students list within the Search Button
  const [searchValues, setSearchValues] = useState({
    departmentName: specificData?.departmentName || '',
    className: specificData?.className || '',
    sectionName: specificData?.sectionName || '',
    month: specificData?.month || new Date().getMonth() + 1,
  });

  const getPercentageDisplay = (percentage) => {
    if (percentage == '-') {
      return '-';
    } else {
      const colorClass =
        percentage - 0 >= 80
          ? 'text-green-600'
          : percentage - 0 >= 60
            ? 'text-orange-300'
            : 'text-red-600';
      return (
        <Typography variant='body2' className={`font-medium ${colorClass}`}>
          {percentage}%
        </Typography>
      );
    }
  };
  const statusDisplay = (status) => {
    const colorClass =
      status == 'P'
        ? 'bg-green-100'
        : status == 'A'
          ? 'bg-red-100'
          : status == 'S'
            ? 'bg-blue-100'
            : status == 'H'
              ? 'bg-orange-100'
              : '';
    return <div className={`${colorClass} px-2 py-1 rounded`}>{status}</div>;
  };
  const filterStudents = useMemo(
    () =>
      debounce((text) => {
        if (text) {
          const data = students?.filter(
            (student) =>
              (student?.rollNumber + '')
                ?.toLowerCase()
                .includes(text.toLowerCase()) ||
              student?.name
                .toLowerCase()
                .toString()
                .includes(text.toLowerCase()),
          );
          setFilteredData(data);
        } else {
          setFilteredData(students);
        }
      }, 600),
    [students],
  );
  const months = {
    1: 'January',
    2: 'February',
    3: 'March',
    4: 'April',
    5: 'May',
    6: 'June',
    7: 'July',
    8: 'August',
    9: 'September',
    10: 'October',
    11: 'November',
    12: 'December',
  };

  function dispalyHeader(buttonType) {
    let headers = [];
    if (buttonType == 'daily') {
      const monthNumber = searchValues?.month ?? 3;
      const monthName = months[monthNumber];
      const year =
        monthNumber >= 6
          ? new Date().getFullYear()
          : new Date().getFullYear() + 1;
      const noDays = new Date(year, monthNumber, 0).getDate();
      for (let i = 1; i <= noDays; i++) {
        headers?.push(`${monthName?.slice(0, 3)} ${i}`);
      }
    } else {
      if (AcademicYearMonths?.length > 0 && Array.isArray(AcademicYearMonths)) {
        headers = AcademicYearMonths?.map((item) => item?.split(' ')[0]);
      } else {
        headers = [
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
        ];
      }
    }
    return headers;
  }

  async function downloadStudents(classDetails) {
    try {
      if (!(classDetails.departmentName && classDetails.className)) {
        throw new Error();
      }
      const fileResponse = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: '/attendance/downloadExcel',
        method: 'GET',
        responseType: 'blob',
        headers: { ...classDetails, instituteType: instituteType },
      });
      const blob = new Blob([fileResponse?.data], {
        type: fileResponse.headers['content-type'],
      });
      let filename = 'AttendanceReports.xlsx';
      const contentDisposition = fileResponse?.headers?.filename;
      filename = contentDisposition;

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      setIsDownloading(false);
    } catch (err) {
      setDownloadError(true);
      setIsDownloading(false);
      console.error('Download failed', err);
    }
  }

  async function getStudents(searchValues) {
    setIsSearching(true);
    let response;
    if (buttonType.trim() == 'daily') {
      if (
        searchValues.month == specificData?.month &&
        searchValues.departmentName === specificData?.departmentName &&
        searchValues.className === specificData?.className &&
        (searchValues?.sectionName
          ? searchValues.sectionName === searchValues.sectionName
          : true)
      ) {
        response = { data: specificData, status: 200 };
      } else {
        response = await dispatch(getDailyReports(searchValues)).unwrap();
      }
    } else if (buttonType.trim() == 'monthly') {
      if (
        searchValues.departmentName === specificData?.departmentName &&
        searchValues.className === specificData?.className
      ) {
        response = { data: specificData, status: 200 };
      } else {
        response = await dispatch(getMonthlyReports(searchValues)).unwrap();
      }
    }
    if (response.status == 200) {
      setStudents(response.data.students);
      setFilteredData(response.data.students);
      setTotalWorkingDays(response.data.totalNumberOfDays);
    } else {
      if (response.status == 409)
        showAlert(
          'Current date is not in between academic year start-date and end-date. Please add <b>Academic Year.</b>',
        );
      else alert('Error in Getting Students!.');
    }
    setIsSearching(false);
  }
  const TableRowMemo = React.memo(({ student, buttonType }) => {
    return (
      <TableRow
        key={student?.id}
        hover
        className='*:py-1 *:my-1 group hover:bg-gray-100'
      >
        <TableCell className='sticky left-0 bg-white max-w-32 group-hover:bg-gray-100 '>
          <Typography variant='body2' className='font-mono '>
            {student?.rollNumber}
          </Typography>
        </TableCell>

        <TableCell className='max-w-40 '>
          <Typography variant='body2'>{student?.name}</Typography>
        </TableCell>

        <TableCell className='max-w-32'>{student?.totalPresentDays}</TableCell>
        <TableCell className='max-w-32'>
          {getPercentageDisplay(student?.attendancePercentage)}
        </TableCell>

        {buttonType.trim().toLowerCase() === 'daily' &&
          typeof student?.record?.[0] === 'string' &&
          student?.record?.map((status, index) => (
            <TableCell key={index}>{statusDisplay(status)}</TableCell>
          ))}

        {buttonType.trim().toLowerCase() === 'monthly' &&
          student?.record?.map((obj, index) => (
            <TableCell key={index}>
              {getPercentageDisplay(obj.percentage)}
            </TableCell>
          ))}
      </TableRow>
    );
  });
  TableRowMemo.displayName = 'TableRowMemo';
  const generateTableBody = useMemo(() => {
    return filteredData?.map((student) => (
      <TableRowMemo
        key={student.id}
        student={student}
        buttonType={buttonType}
      />
    ));
  }, [filteredData, buttonType]);
  useEffect(() => {
    if (
      searchValues?.departmentName &&
      searchValues?.className &&
      searchValues?.month &&
      (searchValues?.sectionName ? searchValues.sectionName : true)
    ) {
      getStudents(searchValues);
    } else {
      setIsSearching(false);
    }
    (async () => {
      if (classes == 0) {
        const response = await dispatch(getClasses()).unwrap();
        if (response !== 'error') {
          setClassDetails(response);
          await dispatch(SetClassesInfo(response));
        }
        setClasses(1);
      }
    })();
  }, [searchValues, buttonType]);
  async function getMonthNames() {
    const monthsData = await dispatch(getMonths()).unwrap();
    setAcademicYearMonths(monthsData);
  }
  useEffect(() => {
    getMonthNames();
  }, []);
  return (
    <>
      <Card className='pl-0 mr-auto rounded shadow-none '>
        <CardContent className='md:p-6 '>
          <Typography
            variant='h6'
            className='mb-2 font-medium '
            color='primary'
          >
            Select Class Details
          </Typography>

          <Formik
            enableReinitialize
            initialValues={searchValues}
            validationSchema={Yup.object({
              departmentName: Yup.string().required(`Select ${Department} `),
              className: Yup.string().required(`Select ${Class} `),
              sectionName:
                instituteType?.toLowerCase() == 'college'
                  ? Yup.string().required(`Select ${Section}`)
                  : Yup.string(),
            })}
            onSubmit={async (values) => {
              if (buttonType.trim() == 'daily') {
                await dispatch(getDailyReports(searchValues)).unwrap();
              } else {
                await dispatch(getMonthlyReports(searchValues)).unwrap();
              }
              setIsSearching(true);
              getStudents({ ...values });
              setSearchValues({ ...values });
            }}
          >
            {({ setFieldValue }) => (
              <Form>
                <Box className='grid grid-cols-1 min-[550px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 gap-y-0 max-w-[1000px] '>
                  <Field name='departmentName'>
                    {({ field, meta }) => (
                      <FormControl
                        fullWidth
                        size='small'
                        error={meta.touched && Boolean(meta.error)}
                      >
                        <InputLabel id='class-label' className='font-medium'>
                          {Department}
                        </InputLabel>
                        <Select
                          MenuProps={MenuProps}
                          labelId='class-label'
                          {...field}
                          value={field.value}
                          label={Department}
                          onChange={(e) => {
                            const dept = classDetails?.find(
                              (obj) => obj.department === e.target.value,
                            );
                            setCurrentDepartment(dept);
                            setCurrentClasses(dept?.classes);
                            setFieldValue('departmentName', e.target.value);
                            setFieldValue('className', '');
                            setFieldValue('sectionName', '');
                          }}
                        >
                          {classDetails?.length == 0 && (
                            <MenuItem value=''>{` No ${Department} Available`}</MenuItem>
                          )}
                          {(Array.isArray(classDetails)
                            ? classDetails
                            : []
                          )?.map((obj) => (
                            <MenuItem key={obj.id} value={obj.department}>
                              {obj.department}
                            </MenuItem>
                          ))}
                        </Select>
                        {meta.touched && meta.error && (
                          <small className='text-red-600'>{meta.error}</small>
                        )}
                      </FormControl>
                    )}
                  </Field>

                  <Field name='className'>
                    {({ field, meta }) => (
                      <FormControl
                        fullWidth
                        size='small'
                        error={meta.touched && Boolean(meta.error)}
                      >
                        <InputLabel id='class-label' className='font-medium'>
                          {Class}
                        </InputLabel>
                        <Select
                          MenuProps={MenuProps}
                          labelId='class-label'
                          {...field}
                          value={field.value}
                          label={Class}
                          onChange={(e) => {
                            const Class = currentClasses?.find(
                              (obj) => obj.className === e.target.value,
                            );
                            setCurrentSections(Class?.sections);
                            setFieldValue('className', e.target.value);
                            setFieldValue('sectionName', '');
                          }}
                        >
                          {currentClasses?.length == 0 && (
                            <MenuItem value=''>{` --- Select  ${Department} ---`}</MenuItem>
                          )}
                          {currentDepartment?.classes?.map((obj) => (
                            <MenuItem key={obj.id} value={obj.className}>
                              {obj.className}
                            </MenuItem>
                          ))}
                        </Select>
                        {meta.touched && meta.error && (
                          <small className='text-red-600'>{meta.error}</small>
                        )}
                      </FormControl>
                    )}
                  </Field>

                  {instituteType?.toLowerCase() === 'college' && (
                    <Field name='sectionName'>
                      {({ field, meta }) => (
                        <FormControl
                          fullWidth
                          size='small'
                          error={meta.touched && Boolean(meta.error)}
                        >
                          <InputLabel id='class-label' className='font-medium'>
                            {Section}
                          </InputLabel>
                          <Select
                            MenuProps={MenuProps}
                            labelId='class-label'
                            {...field}
                            value={field.value}
                            label={Section}
                            onChange={(e) => {
                              setFieldValue('sectionName', e.target.value);
                            }}
                          >
                            {currentSections?.length == 0 && (
                              <MenuItem value=''>{` --- Select  ${Class} ---`}</MenuItem>
                            )}
                            {currentSections?.map((obj) => (
                              <MenuItem key={obj.id} value={obj.section}>
                                {obj.section}
                              </MenuItem>
                            ))}
                          </Select>
                          {meta.touched && meta.error && (
                            <small className='text-red-600'>{meta.error}</small>
                          )}
                        </FormControl>
                      )}
                    </Field>
                  )}

                  <Button
                    type='submit'
                    color='primary'
                    loading={isSearching}
                    variant='contained'
                    className='h-10 font-medium text-white rounded max-w-fit min-w-20'
                    loadingIndicator={
                      <div className='font-bold'>Loading...</div>
                    }
                  >
                    {!isSearching ? (
                      'Search '
                    ) : (
                      <div className='text-black'>Loading...</div>
                    )}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
          <br />
        </CardContent>
      </Card>
      <>
        <Snackbar
          open={downloadError}
          autoHideDuration={3000}
          onClose={() => {
            setDownloadError(false);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => {
              setDownloadError(false);
            }}
            severity='error'
            variant='filled'
            sx={{ width: '100%' }}
          >
            There is an Error in Downloading the Reports !
          </Alert>
        </Snackbar>
        <Backdrop
          sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
          open={isDownloading}
        >
          <div>
            <div className='text-center'>
              <CircularProgress color='inherit' />
            </div>
            <div className='text-center'>Downloading ..</div>
            <div className='text-center'>{downloadError}</div>
          </div>
        </Backdrop>
        <div className='flex flex-wrap items-end md:ml-6 gap-y-2'>
          {/* Daily Button */}
          <Button
            disableRipple
            className={`border text-black px-3 rounded mr-2 
              transition-all duration-200
              ${buttonType.trim() === 'daily' ? ' bg-[#1976d2] text-white' : 'bg-white text-black'}`}
            onClick={() => {
              if (buttonType === 'monthly') {
                setFilteredData([]);
                setButtonType('daily');
                setIsSearching(true);
                setSearchStudent('');
              }
            }}
            startIcon={
              <CalendarTodayIcon
                className={`transition-all duration-200 
                  ${
                    buttonType.trim() === 'daily' ? 'text-white' : 'text-black'
                  }`}
              />
            }
          >
            Daily
          </Button>
          {/* Monthly Button */}
          <Button
            disableRipple
            disableElevation
            disableFocusRipple
            className={`border mr-2 px-2 rounded 
              transition-colors duration-200
              ${
                buttonType.trim() === 'monthly'
                  ? 'bg-[#1976d2] text-white'
                  : 'bg-white text-black'
              }`}
            onClick={() => {
              if (buttonType === 'daily') {
                setButtonType('monthly');
                setFilteredData([]);
                setIsSearching(true);
                setSearchStudent('');
              }
            }}
            startIcon={
              <CalendarMonthIcon className='text-current transition-colors duration-200' />
            }
          >
            Monthly
          </Button>
          {/* Export Data */}
          <Button
            disabled={!specificData}
            className={` border  px-[10px] rounded  sm:ml-auto mr-2   ${!specificData ? 'shadow-none ' : 'text-white  bg-[#1976d2] shadow-md'}  `}
            onClick={() => {
              setIsDownloading(true);
              downloadStudents({ ...searchValues, attendanceType: buttonType });
            }}
            startIcon={<RiFileExcel2Line />}
          >
            Export Data
          </Button>
        </div>
        <Card className='mt-2 border rounded-lg md:ml-6' sx={{ marginTop: 0 }}>
          <CardContent className='p-0'>
            <Box className='p-6 border-b'>
              <div className='flex flex-wrap justify-between gap-y-3'>
                <div>
                  <Typography variant='h6' color='primary'>
                    Attendance Report
                  </Typography>
                  <Typography variant='body1' className='mt-2'>
                    Total Students : {students?.length || 0}
                  </Typography>

                  <div className='flex flex-wrap items-start mt-2 '>
                    {buttonType === 'daily' && (
                      <div className='flex items-center gap-2'>
                        <Typography>Select Month : </Typography>

                        <Select
                          MenuProps={{
                            PaperProps: { style: { maxHeight: 200 } },
                          }}
                          id='daily'
                          size='small'
                          className='h-8 px-2 mr-4 rounded-md '
                          value={searchValues.month}
                          onChange={async (e) => {
                            setSearchValues((prev) => ({
                              ...prev,
                              month: e.target.value,
                            }));
                            specificData ? await dispatch(UpdateMonth()) : '';
                          }}
                        >
                          {Object.keys(months).map((num) => (
                            <MenuItem key={num} value={num}>
                              {months[num]}
                            </MenuItem>
                          ))}
                        </Select>
                      </div>
                    )}
                    <Typography variant='body1'>
                      Total Working Days : {totalWorkingDays || 0}
                    </Typography>
                  </div>
                </div>

                <div className='flex flex-col gap-y-5'>
                  {buttonType == 'daily' && students?.length > 0 && (
                    <div className=''>
                      <div className='flex items-center gap-2'>
                        <div className='flex items-center gap-2'>
                          <small className='px-2 py-1 bg-green-100 rounded '>
                            P
                          </small>
                          {` : `}
                          <Typography variant='body1'>Present</Typography>
                          {`  `}
                        </div>
                        <div className='flex items-center gap-2'>
                          <small className='px-2 py-1 bg-red-100 rounded '>
                            A
                          </small>
                          {` : `}
                          <text>Absent</text>
                        </div>
                      </div>
                      <div className='flex gap-2 mt-2'>
                        <div className='flex items-center gap-2'>
                          <small className='px-2 py-1 bg-blue-100 rounded '>
                            S
                          </small>
                          {` : `}
                          <text>Sunday</text>
                        </div>
                        {`  `}
                        <div className='flex items-center gap-2'>
                          <small className='px-2 py-1 bg-orange-100 rounded '>
                            H
                          </small>
                          {` : `}
                          <text>Holiday</text>
                        </div>
                      </div>
                    </div>
                  )}
                  {buttonType === 'monthly' && (
                    <div className='flex items-center gap-2'>
                      <small className='px-2 py-1 bg-white border rounded shadow-md '>
                        -
                      </small>
                      {` : `}
                      <Typography variant='body1'>
                        Attendance not Recorded{' '}
                      </Typography>
                    </div>
                  )}
                  <TextField
                    size='small'
                    fullWidth
                    placeholder='Search by Id, Name ....'
                    autoComplete='off'
                    value={searchStudnet}
                    variant='outlined'
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value !== ' ') {
                        setSearchStudent(value);
                        filterStudents(value);
                      }
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position='end'>
                            <SearchIcon className='text-gray-600 size-5' />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </div>
              </div>
            </Box>
            <TableContainer
              className={`overflow-auto ${
                students?.length === 0 ? 'min-h-[200px]' : 'max-h-[60vh]'
              }`}
            >
              <Activity mode={isSearching ? 'visible' : 'hidden'}>
                <div className='flex flex-col justify-center items-center min-h-[200px] border my-8'>
                  <CircularProgress
                    size={60}
                    thickness={2}
                    className='text-blue-700'
                  />
                  <p className='mt-4 font-medium text-blue-700'>
                    Fetching Data...
                  </p>
                </div>
              </Activity>

              <Activity mode={isSearching ? 'hidden' : 'visible'}>
                <Table stickyHeader>
                  <MuiTableHead>
                    <TableRow>
                      <TableCell className='sticky left-0 z-10 font-semibold bg-white max-w-32'>
                        Roll Number
                      </TableCell>
                      <TableCell className='font-semibold min-w-40 '>
                        Student Name
                      </TableCell>
                      <TableCell className='w-40 font-semibold'>
                        Total Present Days
                      </TableCell>
                      <TableCell className='w-40 font-semibold text-nowrap'>
                        Total Attendance %
                      </TableCell>
                      {dispalyHeader(buttonType)?.length > 0 && (
                        <>
                          {dispalyHeader(buttonType)?.map((status, index) => (
                            <TableCell
                              key={index}
                              className='font-semibold min-w-12 '
                            >
                              {status}
                            </TableCell>
                          ))}
                        </>
                      )}
                    </TableRow>
                  </MuiTableHead>

                  <TableBody>
                    {students?.length == 0 ? (
                      <TableRow hover>
                        <TableCell
                          className='text-center text-red-500'
                          colSpan={20}
                        >
                          <Typography variant='body2'>No Rows</Typography>
                        </TableCell>
                        <TableCell colSpan={20}></TableCell>
                      </TableRow>
                    ) : (
                      ''
                    )}
                    {generateTableBody}
                  </TableBody>
                </Table>
              </Activity>
            </TableContainer>
          </CardContent>
        </Card>
      </>
    </>
  );
});
ReportsPage.displayName = 'ReportsPage';
